import { useEffect, useRef, useState } from "react";
import * as Y from "yjs";
import * as syncProtocol from "y-protocols/sync";
import * as awarenessProtocol from "y-protocols/awareness";
import { encoding, decoding } from "lib0";
import { buildWsUrl } from "../lib/ws-url";

/**
 * Y.js collab over WebSocket for the project canvas. Maintains a shared
 * Y.Array<SharedStroke> (the drawing surface) + Awareness (cursors).
 * Exposes mutation helpers so the canvas can stay declarative.
 *
 * Server: apps/api-server/src/lib/canvas-broker.ts.
 */

const MESSAGE_SYNC = 0;
const MESSAGE_AWARENESS = 1;

export interface PeerCursor {
  readonly clientId: number;
  readonly label: string;
  readonly color: string;
  readonly x: number;
  readonly y: number;
}

export interface LocalIdentity {
  readonly label: string;
  readonly color: string;
}

export interface SharedStroke {
  readonly id: string;
  readonly points: ReadonlyArray<{ x: number; y: number }>;
  readonly color: string;
  readonly width: number;
  readonly author: string;
  readonly tool: "pen" | "eraser";
}

type ConnState = "idle" | "connecting" | "open" | "closed" | "error";

export function useCanvasAwareness(projectId: string | null, identity: LocalIdentity) {
  const [state, setState] = useState<ConnState>("idle");
  const [peers, setPeers] = useState<ReadonlyArray<PeerCursor>>([]);
  const [strokes, setStrokes] = useState<ReadonlyArray<SharedStroke>>([]);
  const awarenessRef = useRef<awarenessProtocol.Awareness | null>(null);
  const yStrokesRef = useRef<Y.Array<SharedStroke> | null>(null);
  const docRef = useRef<Y.Doc | null>(null);

  useEffect(() => {
    if (!projectId) {
      setState("idle");
      return;
    }
    let cancelled = false;
    let attempt = 0;
    let ws: WebSocket | null = null;
    let doc: Y.Doc | null = null;
    let awareness: awarenessProtocol.Awareness | null = null;
    let yStrokes: Y.Array<SharedStroke> | null = null;
    let backoffTimer: ReturnType<typeof setTimeout> | null = null;

    function refreshPeers() {
      if (!awareness || !doc) return;
      const me = doc.clientID;
      const remote: PeerCursor[] = [];
      for (const [clientId, st] of awareness.getStates()) {
        if (clientId === me) continue;
        const s = st as { user?: { label?: string; color?: string }; cursor?: { x: number; y: number } };
        if (!s.cursor) continue;
        remote.push({
          clientId,
          label: s.user?.label ?? "anon",
          color: s.user?.color ?? "#a78bfa",
          x: s.cursor.x,
          y: s.cursor.y,
        });
      }
      setPeers(remote);
    }

    function refreshStrokes() {
      if (!yStrokes) return;
      setStrokes(yStrokes.toArray());
    }

    function connect() {
      if (cancelled) return;
      setState("connecting");
      const url = buildWsUrl(`/api/projects/${projectId}/canvas/ws`);
      doc = new Y.Doc();
      docRef.current = doc;
      yStrokes = doc.getArray<SharedStroke>("strokes");
      yStrokesRef.current = yStrokes;
      awareness = new awarenessProtocol.Awareness(doc);
      awarenessRef.current = awareness;
      awareness.setLocalStateField("user", { label: identity.label, color: identity.color });
      ws = new WebSocket(url);
      ws.binaryType = "arraybuffer";

      function broadcastAwareness(changed: number[]) {
        if (!awareness || !ws || ws.readyState !== ws.OPEN) return;
        const enc = encoding.createEncoder();
        encoding.writeVarUint(enc, MESSAGE_AWARENESS);
        encoding.writeVarUint8Array(
          enc,
          awarenessProtocol.encodeAwarenessUpdate(awareness, changed),
        );
        ws.send(encoding.toUint8Array(enc));
      }

      function sendDocUpdate(update: Uint8Array) {
        if (!ws || ws.readyState !== ws.OPEN) return;
        const enc = encoding.createEncoder();
        encoding.writeVarUint(enc, MESSAGE_SYNC);
        syncProtocol.writeUpdate(enc, update);
        ws.send(encoding.toUint8Array(enc));
      }

      const onDocUpdate = (update: Uint8Array, origin: unknown) => {
        // Skip echoes from the wire (origin is `ws` for incoming updates).
        if (origin === ws) return;
        sendDocUpdate(update);
      };
      doc.on("update", onDocUpdate);

      const onStrokesObserve = () => refreshStrokes();
      yStrokes.observe(onStrokesObserve);

      const onAwarenessLocal = ({ added, updated, removed }: { added: number[]; updated: number[]; removed: number[] }) => {
        broadcastAwareness(added.concat(updated, removed));
        refreshPeers();
      };
      awareness.on("update", onAwarenessLocal);

      ws.addEventListener("open", () => {
        if (cancelled) return;
        attempt = 0;
        setState("open");
        // Send sync step 1 so the server merges with our (possibly empty) doc.
        const enc = encoding.createEncoder();
        encoding.writeVarUint(enc, MESSAGE_SYNC);
        syncProtocol.writeSyncStep1(enc, doc!);
        ws!.send(encoding.toUint8Array(enc));
        // Push our awareness up.
        broadcastAwareness([doc!.clientID]);
      });

      ws.addEventListener("message", (e: MessageEvent<ArrayBuffer>) => {
        if (!doc || !awareness) return;
        try {
          const buf = new Uint8Array(e.data);
          const dec = decoding.createDecoder(buf);
          const type = decoding.readVarUint(dec);
          if (type === MESSAGE_SYNC) {
            const enc = encoding.createEncoder();
            encoding.writeVarUint(enc, MESSAGE_SYNC);
            syncProtocol.readSyncMessage(dec, enc, doc, ws);
            if (encoding.length(enc) > 1 && ws && ws.readyState === ws.OPEN) {
              ws.send(encoding.toUint8Array(enc));
            }
          } else if (type === MESSAGE_AWARENESS) {
            awarenessProtocol.applyAwarenessUpdate(
              awareness,
              decoding.readVarUint8Array(dec),
              ws,
            );
            refreshPeers();
          }
        } catch {
          // ignore malformed
        }
      });

      ws.addEventListener("close", () => {
        if (cancelled) return;
        setState("closed");
        setPeers([]);
        doc?.off("update", onDocUpdate);
        yStrokes?.unobserve(onStrokesObserve);
        awareness?.off("update", onAwarenessLocal);
        attempt = Math.min(attempt + 1, 6);
        const delay = Math.min(16_000, 500 * 2 ** (attempt - 1));
        backoffTimer = setTimeout(connect, delay);
      });

      ws.addEventListener("error", () => {
        if (cancelled) return;
        setState("error");
      });
    }

    connect();

    return () => {
      cancelled = true;
      if (backoffTimer) clearTimeout(backoffTimer);
      try {
        awareness?.destroy();
      } catch {
        // ignore
      }
      try {
        doc?.destroy();
      } catch {
        // ignore
      }
      if (ws && ws.readyState !== WebSocket.CLOSED) ws.close();
      awarenessRef.current = null;
      yStrokesRef.current = null;
      docRef.current = null;
      setPeers([]);
      setStrokes([]);
    };
  }, [projectId, identity.label, identity.color]);

  function setLocalCursor(point: { x: number; y: number } | null) {
    const a = awarenessRef.current;
    if (!a) return;
    if (point) a.setLocalStateField("cursor", point);
    else a.setLocalStateField("cursor", null);
  }

  function addStroke(s: SharedStroke) {
    const arr = yStrokesRef.current;
    if (!arr) return;
    arr.push([s]);
  }

  function popStroke() {
    const arr = yStrokesRef.current;
    if (!arr) return;
    const len = arr.length;
    if (len > 0) arr.delete(len - 1, 1);
  }

  function clearStrokes() {
    const arr = yStrokesRef.current;
    if (!arr) return;
    const doc = docRef.current;
    if (doc) doc.transact(() => arr.delete(0, arr.length));
    else arr.delete(0, arr.length);
  }

  return { state, peers, strokes, setLocalCursor, addStroke, popStroke, clearStrokes };
}
